import React, { useState } from "react";
import { topCoinDistributionCardService } from "../services/topCoinDistributionCardService";
import { toast } from "react-toastify";
import { MdFileDownload } from "react-icons/md";

const CARD_TIERS = [
  { name: "Grey", color: "#A7ADB4", defaultValue: 1, label: "1 TopCoin - Grey (#A7ADB4)" },
  { name: "Brown", color: "#CD7F32", defaultValue: 2, label: "2 TopCoins - Brown (#CD7F32)" },
  { name: "Purple", color: "#7545C8", defaultValue: 5, label: "5 TopCoins - Purple (#7545C8)" },
  { name: "Red", color: "#647FA5", defaultValue: 10, label: "10 TopCoins - Red (#647FA5)" },
  { name: "Gold", color: "#E9B928", defaultValue: 20, label: "20 TopCoins - Gold (#E9B928)" },
];

function TopCoinDistributionCardGenerateForm({ onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    prefix: "",
    quantity: "5000",
    tierIndex: 0,
    topCoinValue: 1,
    qrUrlBase: `${process.env.REACT_APP_FRONTEND_URL || window.location.origin}/claim-card`,
  });
  const [loading, setLoading] = useState(false);

  const selectedTier = CARD_TIERS[formData.tierIndex] || CARD_TIERS[0];

  const handleTierChange = (index) => {
    const tier = CARD_TIERS[index];
    setFormData((prev) => ({
      ...prev,
      tierIndex: index,
      topCoinValue: 1,
    }));
  };

  const handleChange = (e) => {
    let { name, value } = e.target;
    if (name === "prefix") {
      value = value.toUpperCase().replace(/[^A-Z]/g, "").substring(0, 6);
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const downloadCSV = (cards, prefix, tierName, value) => {
    const headers = ["CARD NUMBER", "UNIQUE SERIAL NUMBER", "TOPCOIN VALUE", "TIER",  "QR CODE"];
    const rows = cards.map((c) =>
      `"${c.card_number}","${c.serial_number}","${c.top_coin_value}","${c.card_tier}","${c.qr_code_data}"`
    );
    const csvContent = [headers.join(","), ...rows].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `${prefix}_${tierName}_${value}TC_QR_cards.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.prefix || !formData.quantity || !formData.topCoinValue) {
      toast.error("Please fill in all required fields.");
      return;
    }
    if (formData.prefix.length < 4 || formData.prefix.length > 6) {
      toast.error("Prefix must be between 4 and 6 letters.");
      return;
    }

    setLoading(true);
    try {
      const response = await topCoinDistributionCardService.generateCards({
        prefix: formData.prefix,
        quantity: parseInt(formData.quantity, 10),
        card_tier: selectedTier.name,
        card_color: selectedTier.color,
        top_coin_value: parseFloat(formData.topCoinValue),
        qr_url_base: formData.qrUrlBase,
      });

      if (response.status) {
        toast.success(response.message || "TopCoin QR cards generated successfully.");
        downloadCSV(response.data, formData.prefix, selectedTier.name, formData.topCoinValue);
        onSuccess();
      } else {
        toast.error(response.message || "Failed to generate cards");
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Failed to generate cards");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" style={overlayStyle}>
      <div className="modal-content" style={modalStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
          <h3 style={{ margin: 0, color: "#1e3a8a", fontSize: "20px" }}>Create TopCoin Distribution Cards</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: "18px", cursor: "pointer", color: "#64748b" }}>✕</button>
        </div>

        {/* Tier Selector Badges */}
        <div style={{ marginBottom: "18px" }}>
          <label style={{ fontSize: "13px", fontWeight: "600", color: "#374151", display: "block", marginBottom: "8px" }}>
            Select Card Tier
          </label>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "8px" }}>
            {CARD_TIERS.map((tier, idx) => (
              <button
                type="button"
                key={tier.name}
                onClick={() => handleTierChange(idx)}
                style={{
                  padding: "10px 6px",
                  borderRadius: "8px",
                  border: formData.tierIndex === idx ? `2px solid ${tier.color}` : "1px solid #e2e8f0",
                  backgroundColor: formData.tierIndex === idx ? "#f8fafc" : "#fff",
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "6px",
                  boxShadow: formData.tierIndex === idx ? `0 0 0 2px ${tier.color}33` : "none",
                  transition: "all 0.2s",
                }}
              >
                <span
                  style={{
                    width: "20px",
                    height: "20px",
                    borderRadius: "50%",
                    backgroundColor: tier.color,
                    border: "2px solid #fff",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                  }}
                />
                <span style={{ fontSize: "12px", fontWeight: 700, color: "#1e293b" }}>{tier.name}</span>
                {/* <span style={{ fontSize: "11px", color: "#64748b" }}>{tier.defaultValue} TC</span> */}
              </button>
            ))}
          </div>
        </div>

        {/* Selected Tier Visual Summary Banner */}
        {/* <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: `${selectedTier.color}15`,
            border: `1px solid ${selectedTier.color}50`,
            borderRadius: "8px",
            padding: "10px 14px",
            marginBottom: "18px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span
              style={{
                width: "14px",
                height: "14px",
                borderRadius: "50%",
                backgroundColor: selectedTier.color,
              }}
            />
            <span style={{ fontSize: "13px", fontWeight: 600, color: "#1e293b" }}>
              {selectedTier.name} Card ({selectedTier.color})
            </span>
          </div>
          <span style={{ fontSize: "13px", fontWeight: 700, color: selectedTier.color }}>
            {formData.topCoinValue} TopCoin{Number(formData.topCoinValue) === 1 ? "" : "s"}
          </span>
        </div> */}

        <form onSubmit={handleSubmit} style={formStyle}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px" }}>
            <div className="form-group" style={groupStyle}>
              <label style={labelStyle}>Word Prefix (4-6 Letters) *</label>
              <input
                type="text"
                name="prefix"
                value={formData.prefix}
                onChange={handleChange}
                placeholder="e.g. MESH"
                required
                disabled={loading}
                style={inputStyle}
              />
              {/* <small style={{ color: "#64748b", fontSize: "11px" }}>Used to generate card numbering and unique serials.</small> */}
            </div>

            <div className="form-group" style={groupStyle}>
              <label style={labelStyle}>Cards Quantity *</label>
              <input
                type="number"
                name="quantity"
                value={formData.quantity}
                onChange={handleChange}
                min="1"
                max="9999"
                required
                disabled={loading}
                style={inputStyle}
              />
              {/* <small style={{ color: "#64748b", fontSize: "11px" }}>e.g. 5000 cards</small> */}
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px" }}>
            <div className="form-group" style={groupStyle}>
              <label style={labelStyle}>TopCoin Value per Card *</label>
              <input
                type="number"
                step="0.01"
                name="topCoinValue"
                value={formData.topCoinValue}
                onChange={(e) => setFormData((prev) => ({ ...prev, topCoinValue: e.target.value }))}
                min="0.01"
                required
                disabled={loading}
                style={inputStyle}
              />
              {/* <small style={{ color: "#64748b", fontSize: "11px" }}>Value credited to customer upon scanning</small> */}
            </div>

            {/* <div className="form-group" style={groupStyle}>
              <label style={labelStyle}>QR Code Claim URL Base *</label>
              <input
                type="text"
                name="qrUrlBase"
                value={formData.qrUrlBase}
                onChange={(e) => setFormData((prev) => ({ ...prev, qrUrlBase: e.target.value }))}
                required
                disabled={loading}
                style={inputStyle}
              />
              <small style={{ color: "#64748b", fontSize: "11px" }}>URL prefix for customer redemption QR</small>
            </div> */}
          </div>

          <div className="modal-actions" style={actionsStyle}>
            <button type="button" onClick={onClose} style={cancelBtnStyle} disabled={loading}>
              Cancel
            </button>
            <button
              type="submit"
              style={{
                ...submitBtnStyle,
                backgroundColor: "#1d4ed8",
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
              }}
              disabled={loading}
            >
              <MdFileDownload size={18} />
              <span>{loading ? "Generating..." : "Generate Cards & Download CSV"}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const overlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0,0,0,0.5)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 1050,
};

const modalStyle = {
  backgroundColor: "#fff",
  padding: "26px",
  borderRadius: "10px",
  width: "600px",
  maxWidth: "92%",
  boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.2)",
};

const formStyle = { display: "flex", flexDirection: "column", gap: "15px" };
const groupStyle = { display: "flex", flexDirection: "column", gap: "4px" };
const labelStyle = { fontSize: "13px", fontWeight: "600", color: "#374151" };
const inputStyle = { padding: "9px 12px", border: "1px solid #cbd5e1", borderRadius: "6px", fontSize: "13px" };
const actionsStyle = { display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "10px" };
const btnBase = { padding: "10px 18px", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "600", fontSize: "13px" };
const cancelBtnStyle = { ...btnBase, backgroundColor: "#f1f5f9", color: "#475569" };
const submitBtnStyle = { ...btnBase, color: "#fff", textShadow: "0 1px 2px rgba(0,0,0,0.2)" };

export default TopCoinDistributionCardGenerateForm;
